// E11 R3 (WI 605/609) — client-side state for an in-flight / active impersonation session, persisted in
// sessionStorage (cleared on tab close, sign-out, and when the session ends). Two records:
//
//  1. the active session Id — sent as X-LogikSpark-Impersonation-Session-Id by the request interceptor
//     (app/axios/logikspark.ts), which the E9 Stage-4 middleware validates against the persona's active
//     session. The key lives in the shared axios/const layer (like the persona key) so the interceptor and
//     this feature share one definition without a layering cycle.
//
//  2. the pending start — the grant a Start action is mid-flight for. Written BEFORE the start POST so it
//     survives the step-up (RFC-9470 claims-challenge) auth redirect; on return, useImpersonationStart
//     resumes the POST. It carries an attempt count so a token that never satisfies the c1 auth-context
//     can't drive an infinite step-up loop.
export { IMPERSONATION_SESSION_ID_KEY } from '../../../app/axios/const'
import { IMPERSONATION_SESSION_ID_KEY } from '../../../app/axios/const'

const PENDING_START_KEY = 'lsk.impersonation.pendingStart'

// The active session Id, or null when unset / malformed. Mirrors the interceptor's positive-integer guard
// (absent, NaN, <= 0 all read as "no session").
export function getImpersonationSessionId(): number | null {
  const raw = sessionStorage.getItem(IMPERSONATION_SESSION_ID_KEY)
  if (!raw) {
    return null
  }
  const id = Number.parseInt(raw, 10)
  return Number.isInteger(id) && id > 0 ? id : null
}

export function setImpersonationSessionId(sessionId: number): void {
  sessionStorage.setItem(IMPERSONATION_SESSION_ID_KEY, String(sessionId))
}

export function clearImpersonationSession(): void {
  sessionStorage.removeItem(IMPERSONATION_SESSION_ID_KEY)
}

export interface PendingImpersonationStart {
  grantId: number
  // Number of start POSTs issued so far for this grant (1 = the initial click). The resume path gives up
  // once this reaches MAX_STEPUP_ATTEMPTS, so a step-up that never sticks fails loudly instead of looping.
  attempts: number
}

export function getPendingStart(): PendingImpersonationStart | null {
  const raw = sessionStorage.getItem(PENDING_START_KEY)
  if (!raw) {
    return null
  }
  try {
    const parsed = JSON.parse(raw) as PendingImpersonationStart
    if (
      parsed &&
      Number.isInteger(parsed.grantId) &&
      parsed.grantId > 0 &&
      Number.isInteger(parsed.attempts) &&
      parsed.attempts > 0
    ) {
      return parsed
    }
  } catch {
    // fall through — a malformed record is treated as no pending start
  }
  return null
}

export function setPendingStart(grantId: number, attempts: number): void {
  sessionStorage.setItem(PENDING_START_KEY, JSON.stringify({ grantId, attempts }))
}

export function clearPendingStart(): void {
  sessionStorage.removeItem(PENDING_START_KEY)
}
